import { useState, useEffect } from 'react';
import { Table, Button, Select, message, Space, Tag, Modal, Form, Input, Image, Descriptions, Spin, Popconfirm } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const imgSrc = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return (process.env.REACT_APP_API_URL || '') + path;
};

const RealNameAuth = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAuthType, setFilterAuthType] = useState('');
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [form] = Form.useForm();

  useEffect(() => { fetchData(); }, [pagination.current, filterStatus, filterAuthType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getRealNameAuths(pagination.current, pagination.pageSize, { status: filterStatus, auth_type: filterAuthType });
      setData(res.data?.list || []);
      setPagination(prev => ({ ...prev, total: res.data?.total || 0 }));
    } catch (error) { message.error('获取列表失败'); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try { const res = await adminAPI.getRealNameAuthDetail(id); setCurrentRecord(res.data); } catch (error) { message.error('获取详情失败'); }
    finally { setDetailLoading(false); }
  };

  const handleViewDetail = async (record) => {
    setDetailVisible(true);
    setCurrentRecord(record);
    await fetchDetail(record.id);
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try { await adminAPI.approveRealNameAuth(id); message.success('认证已通过'); setDetailVisible(false); fetchData(); }
    catch (error) { message.error('操作失败'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { message.warning('请输入拒绝原因'); return; }
    setActionLoading(true);
    try { await adminAPI.rejectRealNameAuth(currentRecord.id, rejectReason.trim()); message.success('已拒绝'); setRejectModalVisible(false); setRejectReason(''); setDetailVisible(false); fetchData(); }
    catch (error) { message.error('操作失败'); }
    finally { setActionLoading(false); }
  };

  const getStatusTag = (status) => {
    const map = { pending: { color: 'orange', text: '待审核' }, approved: { color: 'green', text: '已通过' }, rejected: { color: 'red', text: '已拒绝' } };
    return <Tag color={(map[status] || map.pending).color}>{(map[status] || map.pending).text}</Tag>;
  };

  const authTypeTag = (t) => t === 'enterprise' ? <Tag color="blue">企业</Tag> : <Tag color="green">个人</Tag>;

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', width: 80 },
    { title: '类型', dataIndex: 'auth_type', width: 70, render: v => authTypeTag(v || 'personal') },
    { title: '姓名/企业', dataIndex: 'real_name', width: 150, ellipsis: true,
      render: (text, r) => r.auth_type === 'enterprise' ? (r.enterprise_name || text) : text },
    { title: '状态', dataIndex: 'status', width: 90, render: s => getStatusTag(s) },
    { title: '申请时间', dataIndex: 'created_at', width: 160, render: t => t ? t.replace('T', ' ').substring(0, 19) : '-' },
    { title: '审核时间', dataIndex: 'reviewed_at', width: 160, render: t => t ? t.replace('T', ' ').substring(0, 19) : '-' },
    { title: '操作', key: 'action', width: 160, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>查看</Button>
          {record.status === 'pending' && (
            <Popconfirm title="确定通过此认证申请？" onConfirm={() => handleApprove(record.id)} okText="确定" cancelText="取消">
              <Button type="link" size="small" icon={<CheckOutlined />} style={{ color: '#52c41a' }}>通过</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="fit-screen-page">
      <div className="page-header">
        <h2 className="page-title">实名认证管理</h2>
      </div>
      <div className="search-bar" style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <Select placeholder="状态筛选" allowClear style={{ width: 120 }} value={filterStatus || undefined}
          onChange={(val) => { setFilterStatus(val || ''); setPagination(p => ({ ...p, current: 1 })); }}>
          <Option value="pending">待审核</Option>
          <Option value="approved">已通过</Option>
          <Option value="rejected">已拒绝</Option>
        </Select>
        <Select placeholder="认证类型" allowClear style={{ width: 120 }} value={filterAuthType || undefined}
          onChange={(val) => { setFilterAuthType(val || ''); setPagination(p => ({ ...p, current: 1 })); }}>
          <Option value="personal">个人认证</Option>
          <Option value="enterprise">企业认证</Option>
        </Select>
        <Button onClick={fetchData}>刷新</Button>
      </div>
      <div className="table-container">
        <Table columns={columns} dataSource={data} rowKey="id" loading={loading}
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
          pagination={{ ...pagination, showSizeChanger: true, showTotal: t => `共 ${t} 条`,
            onChange: (p) => setPagination(prev => ({ ...prev, current: p })) }} />
      </div>

      <Modal title="实名认证详情" open={detailVisible} onCancel={() => setDetailVisible(false)} width={750}
        footer={currentRecord?.status === 'pending' ? (
          <Space>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(currentRecord.id)} loading={actionLoading} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>通过认证</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => { form.resetFields(); setRejectModalVisible(true); }} loading={actionLoading}>拒绝</Button>
            <Button onClick={() => setDetailVisible(false)}>关闭</Button>
          </Space>
        ) : (<Button onClick={() => setDetailVisible(false)}>关闭</Button>)}>
        <Spin spinning={detailLoading}>
          {currentRecord && (
            <div>
              <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="认证ID">{currentRecord.id}</Descriptions.Item>
                <Descriptions.Item label="用户ID">{currentRecord.user_id}</Descriptions.Item>
                <Descriptions.Item label="认证类型" span={2}>{authTypeTag(currentRecord.auth_type || 'personal')}</Descriptions.Item>
                {currentRecord.auth_type === 'enterprise' ? <>
                  <Descriptions.Item label="企业名称" span={2}>{currentRecord.enterprise_name || currentRecord.real_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="统一社会信用代码" span={2}>{currentRecord.credit_code || '-'}</Descriptions.Item>
                </> : <>
                  <Descriptions.Item label="真实姓名">{currentRecord.real_name}</Descriptions.Item>
                  <Descriptions.Item label="身份证号">{currentRecord.id_card_number}</Descriptions.Item>
                </>}
                <Descriptions.Item label="状态" span={2}>{getStatusTag(currentRecord.status)}</Descriptions.Item>
                <Descriptions.Item label="申请时间" span={1}>{currentRecord.created_at?.replace('T', ' ').substring(0, 19)}</Descriptions.Item>
                <Descriptions.Item label="审核时间" span={1}>{currentRecord.reviewed_at ? currentRecord.reviewed_at.replace('T', ' ').substring(0, 19) : '-'}</Descriptions.Item>
                {currentRecord.reject_reason && (
                  <Descriptions.Item label="拒绝原因" span={2}><span style={{ color: '#ff4d4f' }}>{currentRecord.reject_reason}</span></Descriptions.Item>
                )}
              </Descriptions>

              {currentRecord.auth_type === 'enterprise' && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginBottom: 8 }}>营业执照</h4>
                  {currentRecord.business_license ? (
                    <Image src={imgSrc(currentRecord.business_license)} width={400} style={{ border: '1px solid #e8e8e8' }}
                      placeholder={<div style={{ width: 400, height: 260, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>加载中...</div>} />
                  ) : <span style={{ color: '#999' }}>未上传</span>}
                </div>
              )}
            </div>
          )}
        </Spin>
      </Modal>

      <Modal title="拒绝认证申请" open={rejectModalVisible} onCancel={() => setRejectModalVisible(false)} onOk={handleReject}
        okText="确认拒绝" cancelText="取消" confirmLoading={actionLoading}>
        <Form form={form}>
          <Form.Item name="rejectReason" label="拒绝原因" rules={[{ required: true, message: '请输入拒绝原因' }]}>
            <TextArea rows={4} placeholder="请输入拒绝原因（将通知用户）" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          </Form.Item>
        </Form>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#faad14' }}>
          <ExclamationCircleOutlined style={{ marginTop: 4 }} />
          <span>拒绝后，用户将收到通知并需要重新提交认证申请</span>
        </div>
      </Modal>
    </div>
  );
};

export default RealNameAuth;
